import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, VehicleType, type VehicleHazard } from '../../../hazards/HazardDefinition';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { hazardSocket, mirroredHazard, mirroredVariant } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, moduleSides, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type FixedObstaclePlacement, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_03 variant "Dynamisches Fahrzeug (langsam fahrend)" – obstacle straight with a slow tipper
 * truck (design/01_segments/SEG_03_Obstacle_Straight). 40 m instead of 20 m: the 9 m truck needs
 * room to drive. It waits in an outer lane, sounds its horn and rolls towards the runner (moving
 * hazard, see hazards/VehicleModel), meeting the runner at z 16; the other lanes carry the
 * sheet's mix of jumps, slides and lane changes.
 *
 *   TRUCK_LEFT_A  truck lane 0 · R6: L1 scaffold pass (slide) · R26: L2 barrier board (jump) · R36: L1 lane closure
 *   TRUCK_LEFT_B  truck lane 0 · R6: L2 pallets (jump)        · R26: L1 scaffold pass (slide) · R36: L2 container
 *   TRUCK_RIGHT_* = mirrored (truck lane 2)
 */
export const TRUCK_STRAIGHT = {
  length: 40,
  startZ: 33,
  meetZ: 16,
  stopZ: 6,
  rows: { R6: 6, R26: 26, R36: 36 },
} as const;

const W = WORLD_CONFIG;
const ACTIVE_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;
const SOCKET_TRUCK = 'SOCKET_EVT_SLOW_TRUCK';

const TRUCK_LEFT: VehicleHazard = {
  id: 'HAZ_TRUCK_L0',
  kind: HazardKind.Vehicle,
  vehicle: VehicleType.Truck,
  socketId: SOCKET_TRUCK,
  lane: 0,
  startZ: TRUCK_STRAIGHT.startZ,
  meetZ: TRUCK_STRAIGHT.meetZ,
  stopZ: TRUCK_STRAIGHT.stopZ,
};
const TRUCK_RIGHT = mirroredHazard(W, 'HAZ_TRUCK_L2', TRUCK_LEFT);

const place = (row: keyof typeof TRUCK_STRAIGHT.rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({
  spawnPointId: `SOCKET_OBS_${row}_L${lane}`,
  obstacleId,
});

const TRUCK_LEFT_A: ObstacleVariant = {
  id: 'TRUCK_LEFT_A',
  weight: 1,
  placements: [place('R6', 1, 'OBS_SCAFFOLD_PASS_A'), place('R26', 2, 'OBS_BARRIER_BOARD_A'), place('R36', 1, 'OBS_LANE_CLOSURE_A')],
  hazards: [TRUCK_LEFT.id],
};

const TRUCK_LEFT_B: ObstacleVariant = {
  id: 'TRUCK_LEFT_B',
  weight: 1,
  placements: [place('R6', 2, 'OBS_PALLET_A'), place('R26', 1, 'OBS_SCAFFOLD_PASS_A'), place('R36', 2, 'OBS_CONTAINER_A')],
  hazards: [TRUCK_LEFT.id],
};

const SIDES = moduleSides(TRUCK_STRAIGHT.length);

export const SEG_OBSTACLE_TRUCK_40M: SegmentDefinition = {
  id: 'SEG_OBSTACLE_TRUCK_40M',
  kind: SegmentKind.Straight,
  displayName: 'Hindernisgerade mit Lkw 40 m',
  length: TRUCK_STRAIGHT.length,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [{ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } }],

  entry: entryConnector(),
  exits: [straightExit(TRUCK_STRAIGHT.length)],
  lanes: standardLanes(W),

  obstacleSpawnPoints: Object.entries(TRUCK_STRAIGHT.rows).flatMap(([row, z]) => obstacleRow(W, row, z, ACTIVE_TYPES)),
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    TRUCK_LEFT_A,
    TRUCK_LEFT_B,
    mirroredVariant(W, 'TRUCK_RIGHT_A', TRUCK_LEFT_A, [TRUCK_RIGHT.id]),
    mirroredVariant(W, 'TRUCK_RIGHT_B', TRUCK_LEFT_B, [TRUCK_RIGHT.id]),
  ],
  hazards: [TRUCK_LEFT, TRUCK_RIGHT],

  collectibleSpawnPoints: collectibleLanes(W, 1, TRUCK_STRAIGHT.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  ...SIDES,
  dynamicEventSockets: [...SIDES.dynamicEventSockets, hazardSocket(SOCKET_TRUCK, 0, TRUCK_STRAIGHT.meetZ, DynamicEventType.SlowTruck)],

  generation: {
    weight: 0.25,
    minDistance: 200,
    minSegmentsAfterJunction: 1,
    // an SEG_03 variant: never right after the 20 m obstacle straight
    family: 'OBSTACLE_20',
    tags: ['straight', 'obstacles', 'mixed', 'hazard', 'vehicle'],
  },
};
